import { type HTMLElement, parse, type TextNode } from 'node-html-parser';

import { isEmbedCapableUrl } from '../../knowledge-smart-card/utils';
import type {
	CarouselElementType,
	ParsedElementType,
	ParsedLayout,
	SectionElementType,
	SmartLinkElementType,
} from '../types';

const CITATION_REGEX = /\[\^(\d+)\^\]/g;

/**
 * Parses HTML content (including incomplete streaming content) and returns typed JSON structure
 *
 * @param modelOutput - The dynamic layout schema content string (possibly incomplete)
 * @param previousParsedAnswer - Optional previous parsed result to enable reference stability
 * @param sectionHashCache - Optional cache mapping section index to content hash
 * @returns Typed array representing the parsed HTML structure
 */
export function parseLuminaTagsToJSON(
	modelOutput: string,
	previousParsedAnswer?: ParsedLayout,
	sectionHashCache?: Map<number, string>,
): ParsedLayout {
	if (!modelOutput || typeof modelOutput !== 'string') {
		return [];
	}

	try {
		const cleanedContent = cleanStreamingContent(modelOutput);
		const sections = parseHTMLToSections(cleanedContent);
		const stabilizedSections = stabilizeSectionReferences(
			sections,
			previousParsedAnswer,
			sectionHashCache,
		);

		return stabilizedSections;
	} catch {
		return [];
	}
}

/**
 * Cleans streaming content by removing incomplete tags and citation markers
 */
function cleanStreamingContent(content: string): string {
	// Remove inline citation indicators
	// https://atlassian.slack.com/archives/C09KC9NV23W/p1769026048635919?thread_ts=1769020398.811119&cid=C09KC9NV23W
	let cleaned = content.replace(CITATION_REGEX, '');

	// Remove incomplete opening tags at the end
	cleaned = cleaned.replace(/<[^>]*$/, '');

	// Remove unclosed carousel tags to prevent visual flickering
	cleaned = removeUnclosedCarousels(cleaned);

	return cleaned;
}

/**
 * Removes unclosed carousel tags and their contents to prevent rendering children
 * before the carousel wrapper is complete (which causes visual flickering)
 */
function removeUnclosedCarousels(content: string): string {
	const openTags = content.match(/<carousel[^>]*>/g);
	const closeTags = content.match(/<\/carousel>/g);
	const hasUnclosedCarousel = (openTags?.length || 0) > (closeTags?.length || 0);

	if (hasUnclosedCarousel) {
		const lastCarouselIndex = content.lastIndexOf('<carousel');
		if (lastCarouselIndex >= 0) {
			return content.substring(0, lastCarouselIndex);
		}
	}

	return content;
}

/**
 * Parses HTML string into section objects
 */
function parseHTMLToSections(htmlContent: string): SectionElementType[] {
	const root = parse(htmlContent);
	const body = root.querySelector('body') || root;

	const sections: SectionElementType[] = [];
	body.childNodes.forEach((element) => {
		const jsonElement = elementToJson(element as HTMLElement | TextNode);
		if (jsonElement) {
			sections.push(jsonElement as SectionElementType);
		}
	});

	return sections;
}

/**
 * Stabilizes section references by reusing unchanged sections from previous parse
 * This optimization prevents unnecessary re-renders in React components
 */
function stabilizeSectionReferences(
	sections: SectionElementType[],
	previousParsedAnswer?: ParsedLayout,
	sectionHashCache?: Map<number, string>,
): SectionElementType[] {
	// No optimization if no cache provided
	if (!sectionHashCache) {
		return sections;
	}

	// First parse - initialize cache
	if (!previousParsedAnswer || previousParsedAnswer.length === 0) {
		sections.forEach((section, i) => {
			const hash = JSON.stringify(section);
			sectionHashCache.set(i, hash);
		});
		return sections;
	}

	// Subsequent parses - reuse stable references
	const stabilized = sections.map((section, i) => {
		// Check if this section existed in the previous parse
		// If yes: compare hashes to decide whether to reuse or recreate
		// If no: this is a new section being added during streaming
		if (i < previousParsedAnswer.length) {
			const newHash = JSON.stringify(section);
			const prevHash = sectionHashCache.get(i);

			// Reuse previous reference if content unchanged
			if (prevHash === newHash) {
				return previousParsedAnswer[i];
			}

			// Content changed, update cache
			sectionHashCache.set(i, newHash);
			return section;
		}

		// New section, cache its hash
		const newHash = JSON.stringify(section);
		sectionHashCache.set(i, newHash);
		return section;
	});

	// Clean up cache for removed sections
	if (sections.length < previousParsedAnswer.length) {
		for (let i = sections.length; i < previousParsedAnswer.length; i++) {
			sectionHashCache.delete(i);
		}
	}

	return stabilized;
}

function elementToJson(element: HTMLElement | TextNode): ParsedElementType | null {
	// node types: https://dom.spec.whatwg.org/#dom-node-nodetype
	if (element.nodeType === 3) {
		// text node
		const text = element.text.trim();
		return text ? text : null;
	}

	// not an element node: https://dom.spec.whatwg.org/#dom-node-nodetype
	if (element.nodeType !== 1) {
		return null;
	}

	const htmlElement = element as HTMLElement; // text node already processed
	const tagName = htmlElement.tagName.toLowerCase();

	// For md and h tags, preserve raw innerHTML to keep LLM output unchanged
	// This includes any HTML tags like <strong>, <em>, <code>, etc.
	if (tagName === 'md' || tagName === 'h') {
		const content = htmlElement.innerHTML.trim();
		return {
			tag: tagName,
			...(htmlElement.attributes && Object.keys(htmlElement.attributes).length > 0
				? { attributes: htmlElement.attributes }
				: {}),
			children: content ? [content] : [],
		} as ParsedElementType;
	}

	const result: ParsedElementType = {
		tag: tagName,
	} as ParsedElementType;

	// Add attributes if they exist
	if (
		htmlElement.attributes &&
		Object.keys(htmlElement.attributes).length > 0 &&
		typeof result !== 'string'
	) {
		result.attributes = htmlElement.attributes;
	}

	// Process children for other tags (section, carousel, smart-link, etc.)
	const children: Array<ParsedElementType | string> = [];
	htmlElement.childNodes.forEach((child) => {
		// handles text + element nodes
		const childJson = elementToJson(child as HTMLElement | TextNode);
		if (childJson) {
			children.push(childJson);
		}
	});

	if (children.length > 0 && typeof result !== 'string') {
		result.children = children;
	}

	// Set display="two-column" on carousels containing embed-capable smart links
	// to trigger the two-column layout with full-height cards.
	if (tagName === 'carousel' && typeof result !== 'string') {
		enrichCarouselDisplayAttribute(result as CarouselElementType);
	}

	return result as ParsedElementType;
}

/**
 * Sets `display="two-column"` on a carousel if any of the first two smart-link
 * children have an embed-capable URL. This triggers a 2-column layout with
 * full-height cards instead of the default 3-column compact layout.
 *
 * Carousels with an existing `display` attribute are left unchanged.
 */
function enrichCarouselDisplayAttribute(carousel: CarouselElementType): void {
	if (carousel.attributes?.display) {
		return; // respect explicit display attribute if already set
	}

	const children = carousel.children;
	if (!Array.isArray(children)) {
		return;
	}

	// Check if either of the first two children are embed-capable smart links
	const firstTwo = children.slice(0, 2);
	const hasEmbedCapable = firstTwo.some(
		(child: ParsedElementType) =>
			typeof child !== 'string' &&
			child.tag === 'smart-link' &&
			isEmbedCapableUrl((child as SmartLinkElementType).attributes?.url || ''),
	);

	if (hasEmbedCapable) {
		if (!carousel.attributes) {
			carousel.attributes = {} as CarouselElementType['attributes'];
		}
		carousel.attributes!.display = 'two-column';
	}
}
